import React, { useState, useEffect, useRef } from 'react';

// const AudioPreviewWaveform = ({ src }) => {
const AudioPreviewWaveform = ({ src, waveform }) => {
  const [playing, setPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [peaks, setPeaks] = useState(null);

  const audioRef = useRef(null);
  const canvasRef = useRef(null);

  // Decode audio data to extract real amplitude peaks
  useEffect(() => {
    if (waveform && Array.isArray(waveform) && waveform.length > 0) {
      const maxVal = Math.max(...waveform) || 1;
      const normalized = waveform.map((val) => Math.max(0.25, Math.min(1.0, val / maxVal)));
      setPeaks(normalized);
      return;
    }
    if (!src) return;

    let isMounted = true;
    setIsReady(false);
    setPeaks(null);

    const fallbackPeaks = [];
    for (let i = 0; i < 150; i++) {
      fallbackPeaks.push(0.25 + Math.abs(Math.sin(i * 0.10)) * 0.6 + Math.random() * 0.15);
    }

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) {
      setPeaks(fallbackPeaks);
      return;
    }

    const audioCtx = new AudioCtx();

    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error('Network response not ok');
        return res.arrayBuffer();
      })
      .then((arrayBuffer) => {
        if (!isMounted) return;
        return audioCtx.decodeAudioData(arrayBuffer);
      })
      .then((audioBuffer) => {
        if (!isMounted || !audioBuffer) return;
        const rawData = audioBuffer.getChannelData(0);

        const samplesCount = 150;
        const blockSize = Math.floor(rawData.length / samplesCount);
        const filteredData = [];

        for (let i = 0; i < samplesCount; i++) {
          const blockStart = blockSize * i;
          let max = 0;
          for (let j = 0; j < blockSize; j++) {
            const val = Math.abs(rawData[blockStart + j] || 0);
            if (val > max) max = val;
          }
          filteredData.push(max);
        }

        const maxVal = Math.max(...filteredData);
        // const normalized = filteredData.map((val) => (maxVal > 0 ? Math.pow(val / maxVal, 0.5) : 0.15));
        // const finalPeaks = normalized.map((p) => Math.max(0.15, p));
        const sorted = [...filteredData].sort((a, b) => a - b);
        const p90 = sorted[Math.floor(sorted.length * 0.90)] || maxVal || 1;
        const normalized = filteredData.map((val) => {
          if (p90 <= 0) return 0.25;
          const ratio = val / p90;
          return Math.min(1.0, Math.pow(ratio, 0.5));
        });
        const finalPeaks = normalized.map((p) => Math.max(0.25, p));

        if (isMounted) {
          setPeaks(finalPeaks);
        }
      })
      .catch((err) => {
        console.warn('Unable to decode audio data, using fallback peaks:', err);
        if (isMounted) {
          setPeaks(fallbackPeaks);
        }
      })
      .finally(() => {
        if (audioCtx.state !== 'closed') {
          audioCtx.close();
        }
      });

    return () => {
      isMounted = false;
    };
  }, [src]);

  // Audio HTML5 listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleCanPlay = () => {
      setIsReady(true);
      setDuration(audio.duration || 0);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };

    const handlePlayPause = () => {
      setPlaying(!audio.paused);
    };

    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('durationchange', handleCanPlay);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlayPause);
    audio.addEventListener('pause', handlePlayPause);

    audio.load();

    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('durationchange', handleCanPlay);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlayPause);
      audio.removeEventListener('pause', handlePlayPause);
    };
  }, [src]);

  // Draw the waveform peaks onto canvas with progress color coating
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !peaks) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;

    ctx.clearRect(0, 0, width, height);

    const barWidth = 2.2;
    const gap = 1.2;
    const totalBarWidth = barWidth + gap;
    const numBars = Math.floor((width + gap) / totalBarWidth);

    // Interpolate peaks to match the exact number of bars
    const interpolatedPeaks = [];
    for (let i = 0; i < numBars; i++) {
      const index = (i / numBars) * peaks.length;
      const low = Math.floor(index);
      const high = Math.ceil(index);
      const weight = index - low;
      // const val = (1 - weight) * (peaks[low] || 0.15) + weight * (peaks[high] || peaks[low] || 0.15);
      const val = (1 - weight) * (peaks[low] || 0.25) + weight * (peaks[high] || peaks[low] || 0.25);
      interpolatedPeaks.push(val);
    }

    const progress = duration > 0 ? currentTime / duration : 0;

    interpolatedPeaks.forEach((peak, i) => {
      const x = i * totalBarWidth;
      const barHeight = peak * (height - 4);
      const y = (height - barHeight) / 2;

      const isPlayed = (i / numBars) <= progress;

      ctx.fillStyle = isPlayed ? '#3D5A80' : '#5b5b5b6e';
      // ctx.fillStyle = isPlayed ? '#0b57d0' : '#5f6368';

      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2);
      } else {
        ctx.rect(x, y, barWidth, barHeight);
      }
      ctx.fill();
    });
  }, [peaks, currentTime, duration, src]);

  const togglePlay = () => {
    if (!audioRef.current || !isReady) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const handleCanvasClick = (e) => {
    if (!audioRef.current || !canvasRef.current || !isReady) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;

    const percentage = Math.max(0, Math.min(1, clickX / width));
    const newTime = percentage * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Helper to format duration to mm:ss
  const formatSecs = (secs) => {
    if (isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      width: '100%',
      // backgroundColor: 'rgba(0, 120, 255, 0.1)',
      borderRadius: '10px',
      paddingLeft: '12px',
      paddingRight: '12px',
      paddingTop: '6px',
      paddingBottom: '6px',
      height: '38px',
      // border: '1px solid rgba(0, 120, 255, 0.12)',
      boxSizing: 'border-box'
    }}>
      {/* Hidden audio element */}
      <audio ref={audioRef} src={src} preload="auto" style={{ display: 'none' }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {isReady ? (
          <button
            type="button"
            onClick={togglePlay}
            style={{
              display: 'flex',
              height: '24px',
              width: '24px',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              outline: 'none',
              padding: 0,
              color: '#3D5A80'
            }}
          >
            {playing ? (
              <svg width="12" height="14" viewBox="0 0 10 12" fill="none">
                <rect x="0" y="0" width="3" height="12" rx="1" fill="currentColor" />
                <rect x="7" y="0" width="3" height="12" rx="1" fill="currentColor" />
              </svg>
            ) : (
              <svg width="12" height="14" viewBox="0 0 10 12" fill="none" style={{ marginLeft: '2px' }}>
                <path
                  d="M0 1.5V10.5C0 11.3 0.9 11.8 1.6 11.3L9.2 6.8C9.9 6.4 9.9 5.6 9.2 5.2L1.6 0.7C0.9 0.2 0 0.7 0 1.5Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>
        ) : (
          <div style={{
            height: '20px',
            width: '20px',
            borderRadius: '50%',
            border: '2px solid #E5E7EB',
            borderTopColor: '#0b57d0',
            animation: 'spin 1s linear infinite'
          }} />
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'center' }}>
        <canvas
          ref={canvasRef}
          onClick={handleCanvasClick}
          style={{ width: '100%', height: '24px', cursor: 'pointer', display: 'block' }}
        />
      </div>

      {/* Time text indicator */}
      <div style={{
        fontSize: '13px',
        fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
        color: '#3D5A80',
        userSelect: 'none',
        paddingRight: '4px',
        flexShrink: 0,
        fontWeight: '500',
        minWidth: '40px',
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums'
      }}>
        {playing || currentTime > 0 ? formatSecs(currentTime) : formatSecs(duration)}
      </div>
    </div>
  );
};

export default AudioPreviewWaveform;
